import RiMenuDotsFill from '@ben/icons/more-fill';
import Button from '@ben/orion/button';
import IconButton from '@ben/orion/icon-button';
import InlineTextButton from '@ben/orion/inline-text-button';
import Input, { InputVariant } from '@ben/orion/input';
import Menu from '@ben/orion/menu';
import Switch from '@ben/orion/switch';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import type { ButtonProps as MantineButtonProps } from '@mantine/core';
import { createPolymorphicComponent } from '@mantine/core';
import { useState } from 'react';
import type { FieldError } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../../shared/constants';
import ModularStepComponentWrapper from '../../steps/modular-step-component-wrapper';

export interface ModularDropdownInputOptionProps {
  componentControllerName: string;
  error: FieldError | undefined;
  isMultiSelectInput?: boolean;
  hasSubform?: boolean;
  onRemove: () => void;
}

const ModularDropdownInputOption = ({
  componentControllerName,
  error,
  onRemove,
  isMultiSelectInput = false,
  hasSubform = false,
}: ModularDropdownInputOptionProps) => {
  const { control, setValue } = useFormContext();
  const [menuOpened, setMenuOpened] = useState(false);

  const optionLabel = useWatch({
    name: `${componentControllerName}.label`,
    exact: true,
  });

  const eligibilityRule = useWatch({
    name: `${componentControllerName}.eligibilityRule`,
    exact: true,
  });

  const hasEligibilityRule = !!eligibilityRule;

  const handleAddVisibilityRule = () => {
    setValue(`${componentControllerName}.eligibilityRule`, {
      query: '',
      isActive: true,
    });
    setMenuOpened(false);
  };

  const handleRemoveVisibilityRule = () => {
    setValue(`${componentControllerName}.eligibilityRule`, undefined);
  };

  return (
    <>
      <ComponentsWrapper>
        <Controller
          name={`${componentControllerName}.rank`}
          control={control}
          render={({ field }) => <Input {...field} name="#Rank" disabled />}
        />
        <Controller
          name={`${componentControllerName}.label`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Option label"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
          rules={{ required: fieldCannotBeBlankErrorMessage }}
        />
        <Controller
          name={`${componentControllerName}.key`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Option key"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
          rules={{ required: fieldCannotBeBlankErrorMessage }}
        />
        <MenuWrapper>
          <Menu
            opened={menuOpened}
            onChange={setMenuOpened}
            withinPortal
            zIndex={9999}
          >
            <Menu.Target>
              <IconButtonWrapper>
                <IconButton
                  icon={RiMenuDotsFill}
                  ariaLabel="Option actions"
                  size="md"
                />
              </IconButtonWrapper>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={handleAddVisibilityRule}>
                <Typography variant={TypographyVariant.bodyMd1}>
                  Add visibility rule
                </Typography>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </MenuWrapper>
        <StyledInlineTextButton onClick={onRemove} label="Remove" />
      </ComponentsWrapper>
      {isMultiSelectInput && (
        <Controller
          name={`${componentControllerName}.description`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Option description (optional)"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              variant={InputVariant.TextArea}
              maxLength={360}
              width="100%"
            />
          )}
        />
      )}
      {hasSubform && (
        <>
          <ModularStepComponentWrapper
            componentControllerName={`${componentControllerName}.components`}
            isSubForm
            subFormType={optionLabel}
          />
          <Divider />
        </>
      )}
      {hasEligibilityRule && (
        <VisibilityRuleWrapper>
          <Controller
            name={`${componentControllerName}.eligibilityRule.isActive`}
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                label="Set rule active"
                defaultValue={field.value}
              />
            )}
          />
          <Controller
            name={`${componentControllerName}.eligibilityRule.query`}
            control={control}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                name="Show when"
                showLabel
                hasError={!!fieldState.error}
                errorMessage={fieldState.error?.message}
                variant={InputVariant.TextArea}
                width="100%"
              />
            )}
          />
          <RemoveRuleButton variant="text" onClick={handleRemoveVisibilityRule}>
            Remove rule
          </RemoveRuleButton>
        </VisibilityRuleWrapper>
      )}
    </>
  );
};

export default ModularDropdownInputOption;

const ComponentsWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
    align-items: center;
  `}
`;

const StyledInlineTextButton = styled(InlineTextButton)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(3)};
  `}
`;

const VisibilityRuleWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
    margin-top: ${theme.spacing(2)};
  `}
`;

const Divider = styled.div`
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.palette.neutral[60]};
    height: 1px;
    width: 100%;
  `}
`;

const RemoveRuleButton = createPolymorphicComponent<
  'button',
  MantineButtonProps
>(styled(Button)`
  align-self: flex-end;
`);

const MenuWrapper = styled.div`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(3)};
  `}
`;

const IconButtonWrapper = styled.div`
  display: flex;
`;
